import { useState } from "react";
import { useT } from "@hooks";

interface EconomyRankingHeaderProps {
  title: string;
  mostEconomicalLabel: string;
  leastEconomicalLabel: string;
  showMostEconomical: boolean;
  onShowMostEconomicalChange: (showMostEconomical: boolean) => void;
}

const SUBTITLE_STYLE = {
  fontFamily: "Source Sans Pro",
  fontWeight: 300,
  fontStyle: "italic",
  fontSize: 14,
  lineHeight: "22.86px",
  letterSpacing: 0,
  verticalAlign: "middle",
} as const;

const FILTER_BUTTON_STYLE = {
  backgroundColor: "transparent",
  border: "none",
  padding: 4,
  cursor: "pointer",
} as const;

const FILTER_ICON_STYLE = { fontSize: 18, color: "#30B4C0" } as const;

const BACKDROP_STYLE = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 999,
  background: "transparent",
  border: "none",
  padding: 0,
} as const;

const MENU_STYLE = {
  position: "absolute",
  top: "100%",
  right: 0,
  marginTop: 8,
  backgroundColor: "var(--color-white)",
  border: "1px solid var(--color-lighter)",
  borderRadius: 4,
  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  minWidth: 180,
  zIndex: 1000,
  padding: "8px 0",
} as const;

const OPTION_STYLE = {
  width: "100%",
  padding: "8px 16px",
  border: "none",
  backgroundColor: "transparent",
  textAlign: "left",
  cursor: "pointer",
  fontSize: 14,
  display: "flex",
  alignItems: "center",
  gap: 8,
} as const;

const highlightOption = (event: React.MouseEvent<HTMLButtonElement>) => {
  event.currentTarget.style.backgroundColor = "var(--color-lightest)";
};

const clearOptionHighlight = (event: React.MouseEvent<HTMLButtonElement>) => {
  event.currentTarget.style.backgroundColor = "transparent";
};

export default function EconomyRankingHeader({
  title,
  mostEconomicalLabel,
  leastEconomicalLabel,
  showMostEconomical,
  onShowMostEconomicalChange,
}: Readonly<EconomyRankingHeaderProps>) {
  const t = useT();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const selectOption = (option: boolean) => {
    onShowMostEconomicalChange(option);
    setIsMenuOpen(false);
  };

  return (
    <div className="display-flex flex-wrap justify-content-between align-items-center padding-bottom-20">
      <div className="display-flex flex-wrap align-items-center gap-10 min-width-0">
        <span className="text-size-16 text-bold">{title}</span>
        <span style={SUBTITLE_STYLE}>{showMostEconomical ? mostEconomicalLabel : leastEconomicalLabel}</span>
      </div>
      <div style={{ position: "relative" }}>
        <button className="btn btn-sm" style={FILTER_BUTTON_STYLE} onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <span className="rioglyph rioglyph-filter" style={FILTER_ICON_STYLE} />
        </button>
        {isMenuOpen && (
          <>
            <button
              type="button"
              aria-label={t("common.action.close")}
              tabIndex={0}
              style={BACKDROP_STYLE}
              onClick={() => setIsMenuOpen(false)}
              onKeyDown={(evento) => {
                if (evento.key === "Enter" || evento.key === " ") {
                  evento.preventDefault();
                  setIsMenuOpen(false);
                }
              }}
            />
            <div style={MENU_STYLE}>
              <button
                style={OPTION_STYLE}
                onClick={() => selectOption(true)}
                onMouseEnter={highlightOption}
                onMouseLeave={clearOptionHighlight}
              >
                <span style={{ color: "#4CAF50" }}>⬆</span>
                {mostEconomicalLabel}
              </button>
              <button
                style={OPTION_STYLE}
                onClick={() => selectOption(false)}
                onMouseEnter={highlightOption}
                onMouseLeave={clearOptionHighlight}
              >
                <span style={{ color: "#FF5722" }}>⬇</span>
                {leastEconomicalLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
